import { Modal } from '../../components/modal/modal';
import { GeneralLedgerAccount } from '../../clients/quipli-service/accounting/accounting';
import { useEffect, useState } from 'react';
import { Button } from '../../components/form/button';
import { useAccountingApiV1 } from '../../clients/quipli-service/accounting/accounting-api-v1';
import { toast } from '../../util/toasts';
import { SelectForm } from '../../components/select-form/select-form';
import { TaxCategory, TaxJurisdiction } from '../../clients/quipli-service/tax/tax';
import { useTaxApiV1 } from '../../clients/quipli-service/tax/tax-api-v1';

export const TaxJurisdictionModal = ({
  isOpen,
  close,
  jurisdiction,
}: {
  isOpen: boolean;
  close: (isWrite: boolean) => void;
  jurisdiction?: TaxJurisdiction;
}) => {
  const { createTaxJurisdiction, updateTaxJurisdiction, searchTaxCategories } = useTaxApiV1();
  const { listAccounts } = useAccountingApiV1();

  const defaultJurisdictionRequest = {
    name: '',
    percentage: 0,
    taxCategories: [],
  };
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [jurisdictionRequest, setJurisdictionRequest] = useState<{
    name: string;
    percentage: number;
    generalLedgerAccountId?: string;
    taxCategories: TaxCategory[];
  }>(defaultJurisdictionRequest);
  const [glAccounts, setGlAccounts] = useState<GeneralLedgerAccount[]>([]);
  const [taxCategories, setTaxCategories] = useState<TaxCategory[] | undefined>();

  async function doSave() {
    try {
      setIsSaving(true);
      if (jurisdiction) {
        await updateTaxJurisdiction({ id: jurisdiction.id, ...jurisdictionRequest });
        toast('Jurisdiction updated successfully');
      } else {
        await createTaxJurisdiction(jurisdictionRequest);
        toast('Jurisdiction created successfully');
      }
      close(true);
      clearForm();
    } catch (e: any) {
      toast('Failed to save jurisdiction', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function init() {
    try {
      const glAccountResp = await listAccounts();
      setGlAccounts(glAccountResp);
      const categories = await searchTaxCategories({});
      setTaxCategories(categories);
    } catch (e: any) {
      toast('Failed to fetch GL accounts', 'error');
    }
  }

  function doClose() {
    close(false);
    clearForm();
  }

  function clearForm() {
    setJurisdictionRequest(defaultJurisdictionRequest);
  }

  useEffect(() => {
    jurisdiction &&
      setJurisdictionRequest({
        name: jurisdiction.name,
        percentage: jurisdiction.percentage,
        generalLedgerAccountId: jurisdiction.generalLedgerAccountId,
        taxCategories: jurisdiction.taxCategories,
      });
  }, [jurisdiction]);

  useEffect(() => {
    init();
  }, []);

  return (
    <Modal
      title={jurisdiction ? 'Update Jurisdiction' : 'Create Jurisdiction'}
      isOpen={isOpen}
      close={doClose}
      actions={
        <>
          <Button className="button button-secondary" disabled={isSaving} onClick={doClose}>
            Cancel
          </Button>
          <Button className="button button-yellow" disabled={isSaving} onClick={doSave}>
            {jurisdiction ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="flex flex-column gap-2">
        {jurisdiction?.source !== 'TaxJar' && (
          <>
            <div>
              <label>Jurisdiction Name </label>
              <input
                type="text"
                value={jurisdictionRequest.name}
                onChange={(e) =>
                  setJurisdictionRequest((prev) => {
                    return { ...prev, name: e.target.value };
                  })
                }
              />
            </div>

            <div>
              <label>Allocated Percentage </label>
              <input
                type="number"
                value={jurisdictionRequest.percentage}
                onChange={(e) =>
                  setJurisdictionRequest((prev) => {
                    return { ...prev, percentage: parseFloat(e.target.value) };
                  })
                }
              />
            </div>
          </>
        )}
        <div>
          <label>General Ledger Account</label>
          <SelectForm
            menuShouldBlockScroll
            menuPosition="fixed"
            menuPortalTarget={document.body}
            value={
              jurisdictionRequest.generalLedgerAccountId
                ? {
                    value: jurisdictionRequest.generalLedgerAccountId,
                    label: glAccounts.find((account) => account.id == jurisdictionRequest.generalLedgerAccountId)?.name,
                  }
                : undefined
            }
            options={glAccounts.map((account) => ({ value: account.id, label: account.name }))}
            onChange={(selected) =>
              setJurisdictionRequest((prev) => {
                return { ...prev, generalLedgerAccountId: selected?.value };
              })
            }
          />
        </div>
        {taxCategories && (
          <div>
            <label>Tax Categories</label>
            <SelectForm
              menuShouldBlockScroll
              menuPosition="fixed"
              menuPortalTarget={document.body}
              isMulti={true}
              value={taxCategories
                .filter((t) => jurisdictionRequest.taxCategories.find((tc) => tc.id == t.id))
                .map((t) => {
                  return { value: t.id, label: t.name };
                })}
              options={taxCategories.map((c) => {
                return { value: c.id, label: c.name };
              })}
              onChange={(selected) =>
                setJurisdictionRequest((prev) => {
                  return { ...prev, taxCategories: selected.map((s) => taxCategories.find((t) => t.id == s.value)!) };
                })
              }
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
